// Socket.IO client for the backend's /live-tracking namespace. The server
// authenticates the handshake with the company token (auth.token); a refused
// handshake surfaces as `connect_error` whose message starts "Unauthorized" or
// "Forbidden". Every client → server event replies through an ack with the
// API's usual { success, message, data | error } envelope.

import { io, type Socket } from 'socket.io-client';
import { SOCKET_URL } from '@/config/config';

/** Event names — mirror the backend's SocketEvents. */
export const LiveTrackingEvents = {
  /** → server: start receiving updates for my linked users (ack: snapshot). */
  Subscribe: 'live-tracking:subscribe',
  /** → server: ask my linked users' apps for their current location
   *  (ack: { requested }); the fixes then arrive as `location:updated`. */
  RequestLocations: 'location:request',
  /** ← server: a linked user reported new GPS fixes. */
  LocationUpdated: 'location:updated',
  /** ← server: a linked user's clock sync — every synced clock and the
   *  clock-in/out positions it stored, in one event. */
  AttendanceUpdated: 'attendance:updated',
} as const;

export interface SocketAck<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: { msg?: string };
}

/**
 * Open a socket for the given company token. Websocket transport only: the
 * API runs on several instances behind a load balancer, and long-polling
 * would need sticky sessions. Reconnects automatically (with the same token);
 * when the token changes, the caller closes this socket and opens a new one.
 */
export const connectLiveTracking = (token: string): Socket =>
  io(`${SOCKET_URL}/live-tracking`, {
    transports: ['websocket'],
    auth: { token },
  });

/** Whether a connect_error is the server refusing the token (vs. network). */
export const isAuthRejection = (error: Error): boolean =>
  /^(Unauthorized|Forbidden)\b/.test(error.message);
